import { type SyntheticEvent } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { CircleAlertIcon, CornerLeftUpIcon, PlusIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { RadioCard } from "simplydo/react/components/RadioCard/RadioCard";
import { RichTextViewer } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxWhenChecked,
  CheckboxContent,
  CheckboxHeader,
  CheckboxChildren,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useAppSelector } from "store";

import { UserChip } from "components/lib/Chips";
import { getImpactTypeIcon } from "components/lib/Ideas/ImpactStatements/impactIcons";

import type {
  DropdownFieldValue,
  IdeasLibUser,
  ImpactStatementWithCreator,
  OrganisationImpact,
} from "../types";
import { dropdownStringValue } from "../types";
import { ImpactStatementCategories } from "./ImpactStatementCategories";
import { mapOrganisationImpacts } from "./mapOrganisationImpacts";
import { formatOption, impactStageDefinitions } from "./statementUtils";
import { useImpactStatements } from "./useImpactStatements";

type ImpactStatementFormProps = {
  ideaId: string;
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  rangeInputType?: "auto" | "allowCustom";
  showPreviousStatement?: boolean;
};

const HelpText = ({
  stage,
  helpText,
}: {
  stage: OpenAPI.Schemas["ImpactStatement"]["stage"];
  helpText?: Record<string, string | undefined>;
}) => {
  if (!helpText || !(stage in helpText) || !helpText[stage]) {
    return null;
  }
  return <RichTextViewer value={helpText[stage]} />;
};

const PreviousEstimateNote = ({
  impact,
  previousStatement,
}: {
  impact: OrganisationImpact;
  previousStatement: ImpactStatementWithCreator;
}) => {
  const { t } = useTranslation();
  const previousImpact = previousStatement.impacts?.[impact._id];

  return (
    <CheckboxChildren>
      <div>
        <strong>Previous estimate:</strong> <br />
        {previousImpact?.enabled ? (
          <>
            <UserChip user={previousStatement.ownerCreatedBy} compact="very" />
            {previousImpact?.choice?.value
              ? ` estimated "${previousImpact.choice.value}"`
              : null}
            {previousImpact?.frequency ? ` ${previousImpact.frequency}` : null}.
            <br />
            {previousImpact?.justification
              ? `Their comment: "${previousImpact.justification}"`
              : null}
          </>
        ) : (
          <>
            <UserChip user={previousStatement.ownerCreatedBy} compact="very" />
            {` did not indicate this ${t("generic.impact")}.`}
          </>
        )}
      </div>
    </CheckboxChildren>
  );
};

export const ImpactStatementForm = ({
  ideaId,
  stage,
  rangeInputType,
  showPreviousStatement,
}: ImpactStatementFormProps) => {
  const { t } = useTranslation();
  const user: IdeasLibUser = useAppSelector((state) => state.user);
  const organisationId = user?.ownerOrganisation?._id;
  const {
    impactStatements,
    statementDefinition,
    creatingStage,
    createImpactStatement,
    patchImpactStatement,
    patchStatementImpact,
  } = useImpactStatements(ideaId);
  const { data: mappedImpactCategories = [] } = useQuery(
    initUseQuery(api.organisations.getImpacts, [organisationId!], {
      isEnabled: !!organisationId,
      select: mapOrganisationImpacts,
    }),
  );

  const impactStatement = impactStatements[stage] as
    | ImpactStatementWithCreator
    | undefined;
  const previousStage = impactStageDefinitions[stage]?.previousStage;
  const previousStatement = (
    showPreviousStatement && previousStage
      ? impactStatements[previousStage]
      : undefined
  ) as ImpactStatementWithCreator | undefined;

  if (!impactStatement) {
    return (
      <div key={stage}>
        <HelpText stage={stage} helpText={statementDefinition?.helpText} />
        <Divider section />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Button
            loading={creatingStage === stage}
            onClick={() => {
              createImpactStatement(stage);
            }}
            color="secondary"
          >
            <Icon icon={PlusIcon} />
            Begin {t("generic.impact")} statement
          </Button>
        </div>
      </div>
    );
  }

  if (!statementDefinition) {
    return null;
  }

  return (
    <div key={stage}>
      <HelpText stage={stage} helpText={statementDefinition.helpText} />
      <Divider hidden />
      <ImpactStatementCategories
        stage={stage}
        statementDefinition={statementDefinition}
        impactStatement={impactStatement}
        mappedImpactCategories={mappedImpactCategories}
        renderImpact={(impact) => {
          const statementImpact = impactStatement.impacts?.[impact._id];
          const impactDef = statementDefinition.impacts?.[impact._id];

          return (
            <Checkbox
              toggle
              style={{ marginBottom: 0 }}
              checked={statementImpact?.enabled}
              onChange={() => {
                patchStatementImpact(stage, impact._id, (prev) => ({
                  ...prev,
                  enabled: !prev?.enabled,
                }));
              }}
            >
              <CheckboxHeader>
                {impact.icon ? (
                  <Icon icon={getImpactTypeIcon(impact.type)} />
                ) : null}
                {impact.name}
                {impact?.unit && <span> ({impact.unit})</span>}
              </CheckboxHeader>
              {impact.description ? (
                <CheckboxContent>
                  <span style={{ whiteSpace: "pre-line" }}>
                    {impact.description}
                  </span>
                </CheckboxContent>
              ) : null}
              {previousStatement ? (
                <PreviousEstimateNote
                  impact={impact}
                  previousStatement={previousStatement}
                />
              ) : null}
              <CheckboxWhenChecked>
                <div>
                  {impactDef?.options || impactDef?.customOptionEnabled ? (
                    <RadioCard.Group
                      editable
                      defaultValue={
                        statementImpact?.choice?.custom
                          ? "__custom"
                          : (statementImpact?.choice?.id ?? "")
                      }
                      onValueChange={(value) => {
                        patchStatementImpact(stage, impact._id, (prev) => ({
                          ...prev,
                          choice:
                            value === "__custom"
                              ? { custom: true }
                              : { id: value },
                        }));
                      }}
                    >
                      {impactDef?.options?.map((option) => {
                        const optionValue = formatOption(impact, option);
                        return (
                          <RadioCard.Item key={option.id} value={option.id}>
                            {optionValue}
                            {impact?.unit && <span> ({impact.unit})</span>}
                            {stage !== "userEstimate" &&
                            option.weight !== undefined ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "0.85em",
                                }}
                              >
                                <Icon
                                  style={{ marginRight: 2 }}
                                  icon={CornerLeftUpIcon}
                                  size="small"
                                />
                                <div>Weight: {option.weight || 0}</div>
                              </div>
                            ) : null}
                          </RadioCard.Item>
                        );
                      })}
                      {impactDef?.customOptionEnabled ||
                      rangeInputType === "allowCustom" ? (
                        <RadioCard.Item value="__custom">Other</RadioCard.Item>
                      ) : null}
                    </RadioCard.Group>
                  ) : null}
                  {statementImpact?.choice?.custom ? (
                    <FormField required>
                      <FormLabel>{`Define value${impact?.unit ? ` (${impact.unit})` : ""}`}</FormLabel>
                      <Input
                        fluid
                        type={
                          impact.type === "numerical" ||
                          impact.type === "money" ||
                          impact.type === "time"
                            ? "number"
                            : "text"
                        }
                        placeholder="Please provide a custom value"
                        value={statementImpact?.choice?.value}
                        onChange={(
                          _e: SyntheticEvent,
                          data: { value?: string | number },
                        ) => {
                          const nextValue = data.value ?? "";
                          patchStatementImpact(stage, impact._id, (prev) => ({
                            ...prev,
                            choice: {
                              custom: true,
                              value:
                                impact.type === "numerical" ||
                                impact.type === "money" ||
                                impact.type === "time"
                                  ? parseFloat(String(nextValue))
                                  : nextValue,
                            },
                          }));
                        }}
                      />
                    </FormField>
                  ) : null}
                  {impactDef?.frequencyEnabled ? (
                    <FormField>
                      <FormLabel>
                        {`${t("common:capitalise", { key: "generic.impact" })} frequency`}
                      </FormLabel>
                      <Dropdown
                        variant="outline"
                        value={statementImpact?.frequency ?? "one-off"}
                        onChange={(
                          _e: SyntheticEvent,
                          data: { value?: DropdownFieldValue },
                        ) => {
                          patchStatementImpact(stage, impact._id, (prev) => ({
                            ...prev,
                            frequency: dropdownStringValue(data.value) as
                              | "weekly"
                              | "one-off"
                              | "monthly"
                              | "annually"
                              | undefined,
                          }));
                        }}
                        options={[
                          { key: "one-off", value: "one-off", text: "One-off" },
                          { key: "weekly", value: "weekly", text: "Weekly" },
                          { key: "monthly", value: "monthly", text: "Monthly" },
                          {
                            key: "annually",
                            value: "annually",
                            text: "Annually",
                          },
                        ]}
                      />
                    </FormField>
                  ) : null}
                  {(!statementImpact?.choice ||
                    (statementImpact?.choice?.custom === true &&
                      !statementImpact?.choice?.value)) &&
                    impactDef?.options && (
                      <Message color="yellow" size="small">
                        <Icon icon={CircleAlertIcon} />
                        <MessageHeader>No value provided</MessageHeader>
                        <MessageContent>
                          Please select or fill in a value for this{" "}
                          {t("generic.impact")} from the options above.
                        </MessageContent>
                      </Message>
                    )}
                  <Message color="blue" size="small">
                    <Icon icon={CircleAlertIcon} />
                    <MessageHeader>Justification notice</MessageHeader>
                    <MessageContent>
                      Please provide additional context for your selection. To
                      ensure proper assessment it is important to provide an
                      explanation of how this {t("generic.impact")} can be
                      measured before and after its implementation.
                    </MessageContent>
                  </Message>
                  <FormField
                    required={statementDefinition.requireJustification}
                  >
                    <FormLabel>Details and justification</FormLabel>
                    <TextArea
                      placeholder={`Why did you choose this ${t("generic.impact")}? How does it connect to your ${t("generic.idea")}? How can the ${t("generic.impact")} of your proposal be measured and evaluated?`}
                      defaultValue={statementImpact?.justification}
                      onChange={(
                        _e: SyntheticEvent,
                        data: { value?: DropdownFieldValue },
                      ) => {
                        patchStatementImpact(stage, impact._id, (prev) => ({
                          ...prev,
                          justification: String(data.value ?? ""),
                        }));
                      }}
                    />
                  </FormField>
                </div>
              </CheckboxWhenChecked>
            </Checkbox>
          );
        }}
      />
      <Divider hidden />
      <H4>Additional comments</H4>
      <TextArea
        placeholder={`Provide any additional context, assumptions, or considerations relating to your estimations in this ${t("generic.impact")} statement...`}
        defaultValue={impactStatement?.description}
        onChange={(
          _e: SyntheticEvent,
          data: { value?: DropdownFieldValue },
        ) => {
          patchImpactStatement(stage, (prev) => ({
            ...prev,
            description: String(data.value ?? ""),
          }));
        }}
      />
    </div>
  );
};
